// Manual Review Interface Component
// Interface for reviewing and editing low-confidence extractions

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  X, 
  AlertTriangle, 
  CheckCircle, 
  Save,
  Calendar,
  MapPin,
  DollarSign,
  User,
  Building,
  Clock
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { updateExtractedData, markAsReviewed } from '@/services/campEmailService';

/**
 * Manual Review Interface Component
 * Allows users to review and edit low-confidence extractions
 */
const ManualReviewInterface = ({ campEmail, isOpen, onClose, onStatusChange }) => {
  const [editedData, setEditedData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize edited data when campEmail changes
  useEffect(() => {
    if (campEmail) {
      setEditedData({
        camp_name: campEmail.camp_name || '',
        camp_organization: campEmail.camp_organization || '',
        camp_dates_start: campEmail.camp_dates_start || '',
        camp_dates_end: campEmail.camp_dates_end || '',
        camp_location: campEmail.camp_location || '',
        camp_address: campEmail.camp_address || '',
        camp_cost: campEmail.camp_cost || '',
        child_name: campEmail.child_name || '',
        registration_deadline: campEmail.registration_deadline || ''
      });
      setHasChanges(false);
    }
  }, [campEmail]);

  /**
   * Handle field change
   */
  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (startField, endField, startValue, endValue) => {
    setEditedData(prev => ({
      ...prev,
      [startField]: startValue,
      [endField]: endValue
    }));
    setHasChanges(true);
  };

  /**
   * Save changes
   */
  const saveChanges = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    try {
      // Filter out empty values and convert types
      const updates = {};
      Object.entries(editedData).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          // Convert cost to number
          if (key === 'camp_cost') {
            updates[key] = parseFloat(value);
          } else {
            updates[key] = value.toString().trim();
          }
        }
      });

      await updateExtractedData(campEmail.id, updates);
      setHasChanges(false);
      
      // Notify parent component
      onStatusChange(campEmail.id, campEmail.user_status);
      
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Mark as reviewed
   */
  const markAsReviewed = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await markAsReviewed(campEmail.id);
      onStatusChange(campEmail.id, 'reviewed');
      onClose();
    } catch (error) {
      console.error('Error marking as reviewed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Hide from review (mark as not a camp email)
   */
  const hideFromReview = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await onStatusChange(campEmail.id, 'hidden');
      onClose();
    } catch (error) {
      console.error('Error hiding from review:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Get confidence score display
   */
  const getConfidenceScore = () => {
    const confidence = campEmail?.extraction_confidence || 0;
    const percentage = Math.round(confidence * 100);
    
    return (
      <Badge variant="destructive" className="bg-red-100 text-red-800">
        <AlertTriangle className="h-3 w-3 mr-1" />
        {percentage}% confidence - Manual review required
      </Badge>
    );
  };

  if (!campEmail) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-gray-900">
                Manual Review Required
              </DialogTitle>
              <p className="text-gray-600 mt-1">
                Our AI had difficulty extracting some information from this email. 
                Please review and correct the details below.
              </p>
              <div className="mt-2">
                {getConfidenceScore()}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-4"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Original Email Context */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Original Email</h3>
            <p className="text-sm text-gray-600">
              <strong>Subject:</strong> {campEmail.email_subject}
            </p>
            <p className="text-sm text-gray-600">
              <strong>From:</strong> {campEmail.sender_name || campEmail.sender_email}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Date:</strong> {new Date(campEmail.email_date).toLocaleDateString()}
            </p>
          </div>

          {/* Editable Fields */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Camp Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Camp Name */}
              <div className="space-y-2">
                <Label htmlFor="camp_name" className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-blue-600" />
                  Camp Name *
                </Label>
                <Input
                  id="camp_name"
                  value={editedData.camp_name}
                  onChange={(e) => handleFieldChange('camp_name', e.target.value)}
                  placeholder="Enter camp name"
                  className={!editedData.camp_name ? 'border-red-300' : ''}
                />
                {!editedData.camp_name && (
                  <p className="text-sm text-red-600">Camp name is required</p>
                )}
              </div>

              {/* Organization */}
              <div className="space-y-2">
                <Label htmlFor="camp_organization" className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-green-600" />
                  Organization
                </Label>
                <Input
                  id="camp_organization"
                  value={editedData.camp_organization}
                  onChange={(e) => handleFieldChange('camp_organization', e.target.value)}
                  placeholder="Enter organization name"
                />
              </div>

              {/* Camp Dates */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-brand-600" />
                  Camp Dates
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="camp_dates_start" className="text-xs text-gray-500">
                      Start Date
                    </Label>
                    <Input
                      id="camp_dates_start"
                      type="date"
                      value={editedData.camp_dates_start}
                      onChange={(e) => handleDateRangeChange(
                        'camp_dates_start', 
                        'camp_dates_end', 
                        e.target.value, 
                        editedData.camp_dates_end
                      )}
                    />
                  </div>
                  <div>
                    <Label htmlFor="camp_dates_end" className="text-xs text-gray-500">
                      End Date
                    </Label>
                    <Input
                      id="camp_dates_end"
                      type="date"
                      value={editedData.camp_dates_end}
                      onChange={(e) => handleDateRangeChange(
                        'camp_dates_start', 
                        'camp_dates_end', 
                        editedData.camp_dates_start, 
                        e.target.value
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Registration Deadline */}
              <div className="space-y-2">
                <Label htmlFor="registration_deadline" className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Registration Deadline
                </Label>
                <Input
                  id="registration_deadline"
                  type="date"
                  value={editedData.registration_deadline}
                  onChange={(e) => handleFieldChange('registration_deadline', e.target.value)}
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="camp_location" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-red-600" />
                  Location
                </Label>
                <Input
                  id="camp_location"
                  value={editedData.camp_location}
                  onChange={(e) => handleFieldChange('camp_location', e.target.value)}
                  placeholder="Enter city, state"
                />
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="camp_address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-600" />
                  Address
                </Label>
                <Input
                  id="camp_address"
                  value={editedData.camp_address}
                  onChange={(e) => handleFieldChange('camp_address', e.target.value)}
                  placeholder="Enter full address"
                />
              </div>

              {/* Cost */}
              <div className="space-y-2">
                <Label htmlFor="camp_cost" className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  Cost
                </Label>
                <Input
                  id="camp_cost"
                  type="number"
                  step="0.01"
                  value={editedData.camp_cost}
                  onChange={(e) => handleFieldChange('camp_cost', e.target.value)}
                  placeholder="Enter cost"
                />
              </div>

              {/* Child Name */}
              <div className="space-y-2">
                <Label htmlFor="child_name" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600" />
                  Child Name
                </Label>
                <Input
                  id="child_name"
                  value={editedData.child_name}
                  onChange={(e) => handleFieldChange('child_name', e.target.value)}
                  placeholder="Enter child's name"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={hideFromReview}
                disabled={isSaving}
              >
                Not a Camp Email
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              
              <Button
                onClick={saveChanges}
                disabled={!hasChanges || isSaving || !editedData.camp_name}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <Icon name="loader-circle" size="sm" className="animate-spin text-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save Changes
              </Button>
              
              <Button
                onClick={markAsReviewed}
                disabled={isSaving || !editedData.camp_name}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Mark as Reviewed
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualReviewInterface;
